import { Helmet } from "react-helmet-async";
import {
  BASE_URL,
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  buildCanonicalUrl,
  buildFullTitle,
  type StaticPageSEO,
} from "../seo/pages";

interface SEOHeadProps {
  // 固定ページの SEO 定義（title / description / keywords / canonicalUrl の既定値になる）
  page?: StaticPageSEO;
  title?: string;
  description?: string;
  keywords?: string;
  // 未指定なら canonical / og:url を出力しない（404 ページ等）
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  noIndex?: boolean;
  // 記事用の追加プロパティ
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  // 構造化データ
  structuredData?: object;
}


export const SEOHead: React.FC<SEOHeadProps> = ({
  page,
  title = page?.title,
  description = page?.description ?? DEFAULT_DESCRIPTION,
  keywords = page?.keywords,
  canonicalUrl = page?.path,
  ogImage,
  ogType = "website",
  noIndex = false,
  publishedTime,
  modifiedTime,
  author = SITE_NAME,
  structuredData,
}) => {
  const fullTitle = buildFullTitle(title);
  const fullCanonicalUrl = canonicalUrl ? buildCanonicalUrl(canonicalUrl) : undefined;
  // OGP画像：指定がなければデフォルト、相対パスの場合はBASE_URLを付与
  const fullOgImage = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`)
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      {/* 基本メタタグ */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}

      {/* hreflang - ページ単位で動的に設定 */}
      {fullCanonicalUrl && <link rel="alternate" hreflang="ja" href={fullCanonicalUrl} />}
      {fullCanonicalUrl && (
        <link rel="alternate" hreflang="x-default" href={fullCanonicalUrl} />
      )}

      {/* Robots */}
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="ja_JP" />

      {/* 記事用OGタグ */}
      {ogType === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === "article" && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@miyakokensetsu" />
      <meta name="twitter:creator" content="@miyakokensetsu" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={fullTitle} />

      {/* 構造化データ (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;

